import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";
import { useMemo, useState } from "react";
import {
  Route,
  Routes,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import useApi from "../../api/apiConfig";
import { mapPatientSortField } from "../../api/filters/patientFilters";
import {
  type BackendFilters,
  createDefaultBackendFilters,
} from "../../utils/backendFilters";
import { practiceSelectionKey } from "../../utils/utils";
import { postPatientPayment } from "./api";
import { useCreatePatient, usePatients } from "./hooks";
import PatientProfileContainer from "./PatientProfileContainer";
import { initialPatientStore, patientsStoreReducer } from "./store";
import PatientsListView from "./views/PatientsListView";
import NewPatientModal from "./views/PatientsListView/NewPatientModal";

interface PatientsDashboardProps {
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  practices: PracticeMessage[];
  selectedPractices: PracticeMessage[];
  supportedSources: SourceMessage[];
}

export { initialPatientStore, patientsStoreReducer };

const NO_PATIENTS: PatientWithClaimMessage[] = [];

export default function PatientsDashboard({
  updateSnackBar,
  practices,
  selectedPractices,
  supportedSources,
}: PatientsDashboardProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getClient } = useApi();
  const createPatientMutation = useCreatePatient();
  const isListRoute = useMatch("/patients") !== null;

  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [backendFilters, setBackendFilters] = useState<BackendFilters | null>(
    null,
  );

  const selectedPracticesKey = practiceSelectionKey(selectedPractices);
  const hasPractices = selectedPractices.length > 0;

  // Reset paging during render so the query never fires with the old page.
  const [prevPracticesKey, setPrevPracticesKey] =
    useState(selectedPracticesKey);
  if (prevPracticesKey !== selectedPracticesKey) {
    setPrevPracticesKey(selectedPracticesKey);
    setPagination((prev) => ({ ...prev, page: 1 }));
  }

  const patientsParams = useMemo(() => {
    const filters: BackendFilters = createDefaultBackendFilters();
    if (backendFilters) {
      filters.filters = { ...backendFilters.filters };
      filters.datagridFilters = [...(backendFilters.datagridFilters ?? [])];
      filters.logicalOperator = backendFilters.logicalOperator;
    }
    filters.filters = {
      ...filters.filters,
      practice_id: selectedPracticesKey ? selectedPracticesKey.split(",") : [],
    };
    return {
      filters,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sortField: sortField ? mapPatientSortField(sortField) : null,
      sortDirection,
    };
  }, [
    backendFilters,
    selectedPracticesKey,
    pagination.page,
    pagination.pageSize,
    sortField,
    sortDirection,
  ]);

  const patientsQuery = usePatients(
    patientsParams,
    hasPractices && isListRoute,
  );

  const createPatient = async (
    patient: PatientMessage,
    paymentMethodToken?: string,
  ) => {
    try {
      const { wieldyId, ...createPatientMessage } = patient;
      const wieldyPatientId =
        await createPatientMutation.mutateAsync(createPatientMessage);
      if (paymentMethodToken) {
        await postPatientPayment(
          await getClient(),
          wieldyPatientId,
          paymentMethodToken,
        );
      }
      updateSnackBar({
        severity: "success",
        message: "Successfully created patient",
      });
      const redirect = searchParams.get("redirect");
      if (redirect) {
        searchParams.delete("redirect");
        const to = { pathname: redirect, search: searchParams.toString() };
        navigate(to);
        return;
      }
      navigate({
        pathname: `/patients/${wieldyPatientId}`,
        search: searchParams.toString(),
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.data) {
        const responseBody = error.response.data;
        updateSnackBar({
          severity: "error",
          message:
            (typeof responseBody.detail === "string"
              ? responseBody.detail
              : responseBody.detail?.[0]?.msg) || "An unknown error occurred.",
        });
      } else {
        updateSnackBar({
          severity: "error",
          message: "An unknown error occurred.",
        });
      }
    }
  };

  const handleBackendFilterChange = (filters: BackendFilters) => {
    setBackendFilters(filters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const onPageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const onPageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
  };

  const onSortChange = (field: string | null, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <PatientsListView
            patients={
              hasPractices
                ? (patientsQuery.data?.data ?? NO_PATIENTS)
                : NO_PATIENTS
            }
            practices={practices}
            patientsLoading={
              hasPractices &&
              (patientsQuery.isPending || patientsQuery.isPlaceholderData)
            }
            pagination={{
              ...pagination,
              rowCount: hasPractices ? (patientsQuery.data?.total ?? 0) : 0,
              totalPages: hasPractices
                ? (patientsQuery.data?.total_pages ?? 0)
                : 0,
            }}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onSortChange={onSortChange}
            onFilterChange={handleBackendFilterChange}
          />
        }
      />
      <Route
        path="/:patientId"
        element={
          <PatientProfileContainer
            updateSnackBar={updateSnackBar}
            practices={practices}
            supportedSources={supportedSources}
          />
        }
      />
      <Route
        path="/new-patient"
        element={
          <NewPatientModal
            updateSnackBar={updateSnackBar}
            onSubmit={createPatient}
            practices={practices}
            onClose={() => {
              navigate(-1);
            }}
            open
          />
        }
      />
    </Routes>
  );
}
